import React, { useRef } from 'react';
import { motion, useScroll, useTransform, MotionValue } from 'framer-motion';
import { SectionFx, FxVariant } from './SectionFx';
// Use React.ReactNode (namespace access) so this type cannot be stripped
// by an import-cleanup process. The default React import is always present.
interface SectionStageProps {
  children: React.ReactNode;
  intensity?: number;
  disabled?: boolean;
  fx?: FxVariant;
}
/**
 * Cinematic section wrapper.
 *
 * Two independent scroll trackers — one tied to the section's TOP edge for
 * entry, one tied to the section's BOTTOM edge for exit — combine to produce
 * a single scale / opacity / blur value.
 *
 * Offsets are tuned so that even SHORT sections (≥ ~20vh tall) reach a full
 * "centered" window where opacity = 1 and scale = 1. Before this tuning,
 * short sections were stuck in a state where entry hadn't finished before
 * exit began, multiplying their values and leaving them perpetually dimmed
 * — which is what made eyebrow bars and labels look invisible.
 *
 *   • Entry tracker fires while the section's TOP crosses from 85% down the
 *     viewport to 40% down. ~45vh of scroll on entry.
 *   • Exit tracker fires while the section's BOTTOM crosses from 60% down
 *     to 15% down. ~45vh of scroll on exit.
 *   • For any section ≥ ~20vh tall, there is a clean middle window where
 *     TOP ≤ 40% AND BOTTOM ≥ 60% — full scale, full opacity, zero blur.
 *
 * Intensity values are also a little gentler than before so content stays
 * READABLE at the cinematic edges, not washed out into invisibility.
 */
export function SectionStage({
  children,
  intensity = 1,
  disabled = false,
  fx
}: SectionStageProps) {
  const ref = useRef<HTMLDivElement>(null);
  // Entry tracker — section's TOP from 85% → 40% of viewport.
  const {
    scrollYProgress: enterY
  } = useScroll({
    target: ref,
    offset: ['start 0.85', 'start 0.4']
  });
  // Exit tracker — section's BOTTOM from 60% → 15% of viewport.
  const {
    scrollYProgress: exitY
  } = useScroll({
    target: ref,
    offset: ['end 0.6', 'end 0.15']
  });
  // Parallax tracker — full extent for slow background drift behind content.
  const {
    scrollYProgress: fullY
  } = useScroll({
    target: ref,
    offset: ['start end', 'end start']
  });
  // Cinematic depth values — tuned for a distinct "page-to-page" feel.
  // Sections fade completely to black (opacity 0) and push back significantly.
  const minScale = 1 - 0.25 * intensity;
  const minOpacity = 0; // Full fade to black between sections
  const maxBlur = 16 * intensity;
  // Entry curves — section comes IN from defocused / smaller / dim.
  const scaleEnter = useTransform(enterY, [0, 1], [minScale, 1]);
  const opacityEnter = useTransform(enterY, [0, 0.7, 1], [minOpacity, 1, 1]);
  const blurEnter = useTransform(enterY, [0, 1], [maxBlur, 0]);
  // Exit curves — section goes OUT to defocused / smaller / dim.
  const scaleExit = useTransform(exitY, [0, 1], [1, minScale]);
  const opacityExit = useTransform(exitY, [0, 0.3, 1], [1, 1, minOpacity]);
  const blurExit = useTransform(exitY, [0, 1], [0, maxBlur]);
  // Combine entry + exit. Because the offsets are tuned so the windows
  // don't overlap for sensible section heights, the multiplication
  // produces a clean "1.0" middle plateau.
  const scale = useTransform([scaleEnter, scaleExit], ([e, x]: number[]) => e * x) as MotionValue<number>;
  const opacity = useTransform([opacityEnter, opacityExit], ([e, x]: number[]) => e * x) as MotionValue<number>;
  const filter = useTransform([blurEnter, blurExit], ([e, x]: number[]) => {
    const sum = e + x;
    // Sub-0.5px blur is imperceptible and harms compositor perf.
    return sum < 0.5 ? 'none' : `blur(${sum.toFixed(2)}px)`;
  }) as MotionValue<string>;
  // Foreground parallax — stronger vertical slide to feel like a new page entering.
  const contentY = useTransform(fullY, [0, 1], [`${120 * intensity}px`, `${-120 * intensity}px`]);
  // Background fx — counter-translate so atmosphere feels deeper.
  const fxY = useTransform(fullY, [0, 1], [`${-12 * intensity}%`, `${12 * intensity}%`]);
  const fxScale = useTransform(fullY, [0, 0.5, 1], [1.12, 1, 1.12]);
  if (disabled) {
    return <div ref={ref} data-id="element-1283">{children}</div>;
  }
  return <div ref={ref} className="relative" data-id="element-1284">
      {/* Background fx — independent parallax behind content.
            Also fades out with the section so effects don't bleed between "pages". */}
      {fx && fx !== 'none' && <motion.div className="absolute inset-0 pointer-events-none overflow-hidden" style={{
      y: fxY,
      scale: fxScale,
      opacity // Fade FX out between sections
    }} data-id="element-1285">
          <SectionFx variant={fx} data-id="element-1286" />
        </motion.div>}

      {/* Foreground content — cinematic zoom + blur + opacity */}
      <motion.div className="relative" style={{
      scale,
      opacity,
      filter,
      y: contentY,
      willChange: 'transform, opacity',
      transformOrigin: 'center center',
      backfaceVisibility: 'hidden'
    }} data-id="element-1287">
        {children}
      </motion.div>
    </div>;
}